import type { ComponentType } from 'react';
import { NavLink, useMatch, useResolvedPath } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface NavItemProps {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  collapsed?: boolean;
  onNavigate?: () => void;
  /** Lo que espera a alguien en este módulo. Sin número, o en cero, no se dibuja nada. */
  count?: number;
}

const BASE = 'flex h-8 items-center gap-2 rounded-md text-sm transition-colors duration-150 ease-out';
const INACTIVE = 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground';
/** The Nexor pill: the item lifts to the panel colour instead of taking a tint. */
const ACTIVE = 'bg-background text-foreground shadow-sm';

export function NavItem({ to, label, icon: Icon, collapsed = false, onNavigate, count }: NavItemProps) {
  // The active class is resolved here rather than through NavLink's callback
  // form: `TooltipTrigger asChild` merges className as a string, and a function
  // reaching it would be stringified into a broken class list.
  const isActive = useMatch({ path: useResolvedPath(to).pathname, end: false }) !== null;

  const link = (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={cn(
        BASE,
        collapsed ? 'relative w-8 justify-center px-0' : 'w-full px-2',
        isActive ? ACTIVE : INACTIVE,
      )}
    >
      <Icon className="size-4 shrink-0" />
      {collapsed ? <span className="sr-only">{label}</span> : <span className="truncate">{label}</span>}
      {count !== undefined && count > 0 && (
        <span
          aria-label={`${count} sin atender`}
          className={cn(
            'grid h-4 min-w-4 place-items-center rounded-full bg-destructive px-1 text-[10px] font-semibold tabular-nums text-destructive-foreground',
            collapsed ? 'absolute right-0.5 top-0.5 h-3.5 min-w-3.5 text-[9px]' : 'ml-auto',
          )}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}
